import { useState, useMemo } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { type ColumnDef } from '@tanstack/react-table'
import { Search, Eye, ChevronLeft, ChevronRight, FileText } from 'lucide-react'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Card, CardContent } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import { cn } from '@/lib/utils'
import { useUsers, useYearlyStats } from '@/hooks/useUsers'

interface UserRow {
  id: number
  name: string
  username: string
  personal_number: string | null
  employment_type: string | null
  role: string
  vacation_days_remaining: number
  vacation_days_total: number
  is_active: number
  show_in_overview: boolean
}

const roleLabels: Record<string, string> = { admin: 'Admin', büro: 'Büro', bauleiter: 'Bauleiter', mitarbeiter: 'Mitarbeiter' }
const roleColors: Record<string, 'default' | 'secondary' | 'success' | 'warning' | 'info'> = { admin: 'default', bauleiter: 'success', mitarbeiter: 'secondary' }

export default function Mitarbeiter() {
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [roleFilter, setRoleFilter] = useState('all')
  const [statsYear, setStatsYear] = useState(new Date().getFullYear())

  const { data: usersData, isLoading: loading } = useUsers(roleFilter !== 'all' ? { role: roleFilter } : undefined)
  const users = ((usersData as UserRow[] | undefined) ?? []).filter(u => u.is_active && u.show_in_overview)

  const { data: yearlyStatsData, isLoading: loadingStats } = useYearlyStats(statsYear)
  const yearlyStats: Record<number, { sick_days: number; vacation_days: number }> = (yearlyStatsData as { stats?: Record<number, { sick_days: number; vacation_days: number }> } | undefined)?.stats ?? {}

  const filteredUsers = useMemo(() => {
    if (!search) return users
    const s = search.toLowerCase()
    return users.filter(u => u.name?.toLowerCase().includes(s) || u.username?.toLowerCase().includes(s))
  }, [users, search])

  const columns: ColumnDef<UserRow>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => <span className="font-medium">{row.original.name}</span>,
    },
    {
      accessorKey: 'personal_number',
      header: 'Per.Nr.',
      sortingFn: (a, b) => {
        const aVal = a.original.personal_number ? parseInt(a.original.personal_number, 10) : Infinity
        const bVal = b.original.personal_number ? parseInt(b.original.personal_number, 10) : Infinity
        return aVal - bVal
      },
      cell: ({ row }) => (
        <span className="text-center text-muted-foreground">{row.original.personal_number || '-'}</span>
      ),
    },
    {
      accessorKey: 'employment_type',
      header: 'Typ',
      cell: ({ row }) => (
        <Badge variant={row.original.employment_type === 'TZ' ? 'warning' : 'info'}>
          {row.original.employment_type || 'VZ'}
        </Badge>
      ),
    },
    {
      accessorKey: 'role',
      header: 'Rolle',
      cell: ({ row }) => (
        <Badge variant={roleColors[row.original.role] || 'secondary'}>
          {roleLabels[row.original.role] || row.original.role}
        </Badge>
      ),
    },
    {
      accessorKey: 'vacation_days_remaining',
      header: 'Resturlaub',
      cell: ({ row }) => {
        const remaining = row.original.vacation_days_remaining ?? 0
        const total = row.original.vacation_days_total ?? 30
        return (
          <span>
            <span className={cn(remaining < 0 && 'font-bold text-destructive', remaining === 0 && 'font-medium text-orange-600')}>
              {remaining}
            </span>
            {' / '}{total}
          </span>
        )
      },
    },
    {
      id: 'sick_days',
      header: () => (
        <div className="flex flex-col items-center gap-1">
          <span>Krank</span>
          <div className="flex items-center gap-1">
            <button
              onClick={(e) => { e.stopPropagation(); setStatsYear(y => y - 1) }}
              className="rounded p-0.5 hover:bg-muted"
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            <span className="min-w-[40px] text-center text-xs font-normal">{statsYear}</span>
            <button
              onClick={(e) => { e.stopPropagation(); setStatsYear(y => y + 1) }}
              className="rounded p-0.5 hover:bg-muted"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </div>
        </div>
      ),
      enableSorting: false,
      cell: ({ row }) => {
        const sickDays = yearlyStats[row.original.id]?.sick_days ?? 0
        return loadingStats
          ? <span className="text-muted-foreground">...</span>
          : <span className={cn(sickDays > 0 && 'font-medium text-yellow-600')}>{sickDays}</span>
      },
    },
    {
      id: 'vacation_taken',
      accessorFn: (row) => yearlyStats[row.id]?.vacation_days ?? 0,
      header: 'Urlaub gen.',
      cell: ({ row }) => {
        const vacDays = yearlyStats[row.original.id]?.vacation_days ?? 0
        return loadingStats
          ? <span className="text-muted-foreground">...</span>
          : <span className={cn(vacDays > 0 && 'font-medium text-orange-600')}>{vacDays}</span>
      },
    },
    {
      id: 'actions',
      header: () => 'Aktionen',
      enableSorting: false,
      cell: ({ row }) => (
        <Link to={`/admin/mitarbeiter/${row.original.id}`} onClick={(e) => e.stopPropagation()}>
          <Button variant="ghost" size="sm"><Eye className="h-4 w-4" /> Details</Button>
        </Link>
      ),
    },
  ], [yearlyStats, loadingStats, statsYear])

  return (
    <div>
      <PageHeader title="Mitarbeiter" description="Übersicht aller aktiven Mitarbeiter" />

      <Card className="mb-6"><CardContent className="flex flex-wrap gap-4 py-4">
        <div className="relative min-w-[200px] max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Suche..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
        </div>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="flex h-9 w-48 rounded-md border border-input bg-white px-3 py-1 text-sm shadow-sm"
        >
          <option value="all">Alle Rollen</option>
          <option value="mitarbeiter">Mitarbeiter</option>
          <option value="bauleiter">Bauleiter</option>
          <option value="admin">Administratoren</option>
        </select>
        <div className="ml-auto">
          <Button variant="outline" onClick={() => window.open(`/api/users/export/mitarbeiterliste?status=active&role=${roleFilter}`, '_blank')}>
            <FileText className="h-4 w-4" /> PDF Export
          </Button>
        </div>
      </CardContent></Card>

      {loading ? (
        <div className="space-y-2">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : (
        <DataTable
          columns={columns}
          data={filteredUsers}
          onRowClick={(user) => navigate(`/admin/mitarbeiter/${user.id}`)}
          emptyText={search ? 'Keine Mitarbeiter gefunden' : 'Keine Mitarbeiter vorhanden'}
        />
      )}
    </div>
  )
}
